import { extendTheme } from '@chakra-ui/react';

const theme = extendTheme({
  fonts: {
    heading: `'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
    body: `'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
  },
  fontSizes: {
    xs:  '11px',
    sm:  '14px',
    md:  '14px',
    lg:  '16px',
    xl:  '18px',
    '2xl': '21px',
    '3xl': '24px',
    '4xl': '28px',
    '5xl': '32px',
    '6xl': '40px',
  },
  colors: {
    brand: {
      50:  '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#2563eb',
      600: '#1e4ed8',
      700: '#1e40af',
      800: '#1e3a8a',
      900: '#172554',
    },
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: '500',
        borderRadius: '8px',
        lineHeight: '1',
        transition: 'all 0.2s ease',
        _focus: { boxShadow: 'none' },
      },
      sizes: {
        lg: { h: '40px', fontSize: '14px', px: '18px' },
        md: { h: '34px', fontSize: '13px', px: '14px' },
        sm: { h: '30px', fontSize: '12.5px', px: '12px' },
        xs: { h: '24px', fontSize: '11px', px: '8px' },
      },
      variants: {
        solid: ({ colorScheme }) => {
          if (colorScheme === 'blue') {
            return {
              bg: 'brand.500',
              color: 'white',
              _hover: {
                bg: '#2563ebe6',
                _disabled: {
                  bg: 'brand.500',
                },
              },
              _active: {
                bg: 'brand.600',
              },
            };
          }
          return {};
        },
        outline: {
          borderWidth: '1px',
          borderColor: '#d1d5db',
          color: '#374151',
          bg: 'white',
          _hover: {
            bg: '#f9fafb',
            borderColor: '#9ca3af',
          },
          _active: {
            bg: '#f3f4f6',
          },
        },
        ghost: {
          color: '#374151',
          _hover: {
            bg: '#f3f4f6',
          },
          _active: {
            bg: '#e5e7eb',
          },
        },
      },
      defaultProps: {
        colorScheme: 'blue',
        size: 'md',
      },
    },

    Input: {
      variants: {
        outline: {
          field: {
            borderColor: '#d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            height: '40px',
            bg: 'white',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            _focus: {
              borderColor: 'brand.500',
              boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)',
              zIndex: 1,
            },
            _hover: {
              borderColor: '#9ca3af',
            },
            _placeholder: {
              color: '#9ca3af',
              fontSize: '14px',
            },
          },
        },
      },
      defaultProps: { variant: 'outline' },
    },

    Select: {
      variants: {
        outline: {
          field: {
            borderColor: '#d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            bg: 'white',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            _focus: {
              borderColor: 'brand.500',
              boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)',
            },
            _hover: {
              borderColor: '#9ca3af',
            },
          },
        },
      },
    },

    Textarea: {
      variants: {
        outline: {
          borderColor: '#d1d5db',
          borderRadius: '8px',
          fontSize: '14px',
          bg: 'white',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          _focus: {
            borderColor: 'brand.500',
            boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)',
          },
          _hover: {
            borderColor: '#9ca3af',
          },
        },
      },
    },

    Table: {
      variants: {
        simple: {
          th: {
            bg: '#f8fafc',
            borderBottomColor: '#e5e7eb',
            fontSize: '12px',
            fontWeight: '600',
            color: '#000000',
            textTransform: 'none',
            letterSpacing: 'normal',
            paddingX: '16px',
            paddingY: '11px',
          },
          td: {
            borderBottomColor: '#f3f4f6',
            fontSize: '14px',
            color: '#1f2937',
            paddingX: '16px',
            paddingY: '13px',
          },
          tr: {
            _hover: { bg: '#fafafa' },
            transition: 'background-color 0.1s ease',
          },
        },
      },
    },

    Menu: {
      baseStyle: {
        list: {
          borderRadius: '10px',
          borderColor: '#e5e7eb',
          boxShadow: '0 10px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
          py: '6px',
          overflow: 'hidden',
          minW: '160px',
        },
        item: {
          fontSize: '14px',
          borderRadius: '6px',
          mx: '6px',
          width: 'calc(100% - 12px)',
          color: '#374151',
          fontWeight: '400',
          _hover: { bg: '#f1f5f9', color: '#2563eb' },
          _focus: { bg: '#f1f5f9', color: '#2563eb' },
        },
        divider: {
          my: '4px',
          borderColor: '#f3f4f6',
        },
      },
    },

    Modal: {
      baseStyle: {
        dialog: {
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)',
        },
        header: {
          fontSize: '17px',
          fontWeight: '600',
          color: '#111827',
          pb: '8px',
          pt: '24px',
          px: '24px',
          letterSpacing: '-0.01em',
        },
        body: {
          px: '24px',
          pb: '4px',
        },
        footer: {
          pt: '16px',
          pb: '20px',
          px: '24px',
          borderTop: '1px solid',
          borderColor: '#f3f4f6',
          gap: '8px',
        },
        closeButton: {
          borderRadius: '8px',
          top: '16px',
          right: '16px',
          color: '#6b7280',
          _hover: { bg: '#f3f4f6', color: '#111827' },
        },
        overlay: {
          bg: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(4px)',
        },
      },
    },

    FormLabel: {
      baseStyle: {
        fontSize: '13px',
        fontWeight: '500',
        color: '#374151',
        mb: '6px',
        letterSpacing: '0.01em',
      },
    },

    Heading: {
      baseStyle: {
        fontFamily: `'Open Sans', sans-serif`,
        fontWeight: '600',
        color: '#111827',
        letterSpacing: '-0.02em',
      },
    },

    Spinner: {
      defaultProps: { color: 'brand.500' },
    },

    Tabs: {
      variants: {
        line: {
          tablist: {
            borderBottomColor: '#e5e7eb',
          },
          tab: {
            fontSize: '14px',
            fontWeight: '500',
            color: '#6b7280',
            borderBottomWidth: '2px',
            _selected: {
              color: 'brand.500',
              borderColor: 'brand.500',
              fontWeight: '600',
            },
            _hover: {
              color: '#374151',
              bg: 'transparent',
            },
          },
        },
      },
    },

    Checkbox: {
      baseStyle: {
        control: {
          borderColor: '#d1d5db',
          borderRadius: '5px',
          _checked: {
            bg: 'brand.500',
            borderColor: 'brand.500',
            _hover: { bg: 'brand.600', borderColor: 'brand.600' },
          },
          _focus: { boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)' },
        },
        label: { fontSize: '14px', color: '#374151' },
      },
    },

    Radio: {
      baseStyle: {
        control: {
          borderColor: '#d1d5db',
          _checked: {
            bg: 'brand.500',
            borderColor: 'brand.500',
            _hover: { bg: 'brand.600', borderColor: 'brand.600' },
          },
          _focus: { boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)' },
        },
        label: { fontSize: '14px', color: '#374151' },
      },
    },

    Switch: {
      baseStyle: {
        track: {
          _checked: { bg: 'brand.500' },
        },
      },
    },

    Divider: {
      baseStyle: { borderColor: '#f3f4f6' },
    },

    Tooltip: {
      baseStyle: {
        bg: '#111827',
        color: 'white',
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: '500',
        px: '10px',
        py: '6px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      },
    },

    Alert: {
      baseStyle: {
        container: {
          borderRadius: '10px',
          fontSize: '14px',
        },
      },
    },

    Popover: {
      baseStyle: {
        content: {
          borderRadius: '12px',
          borderColor: '#e5e7eb',
          boxShadow: '0 10px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
          _focus: { boxShadow: '0 10px 40px rgba(0,0,0,0.08)' },
        },
      },
    },

    Tag: {
      baseStyle: {
        container: {
          borderRadius: '6px',
          fontWeight: '500',
          fontSize: '12px',
        },
      },
    },
  },
});

export default theme;
